import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";

import { AnnotationsFlyout } from "@features/custom-annotations";
import { useAnnotations } from "@features/custom-annotations/hooks/use-annotations";

import { useChartState } from "../hooks/use-chart-state";
import { useDoNotShowCharts } from "../table/contexts";

import { AddChartButton } from "./add-chart-button";
import { Chart, type ChartImperativeRef } from "./chart";

export interface ChartsImperativeRef {
  exportChartData(index: number): void;
}

export const Charts = forwardRef<ChartsImperativeRef>((_, ref) => {
  const [doNotShowCharts] = useDoNotShowCharts();
  const chartsRef = useRef<(ChartImperativeRef | null)[]>([]);

  const setChartRef = useCallback(
    (idx: number, node: ChartImperativeRef | null) => {
      chartsRef.current[idx] = node;
    },
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      exportChartData: (index: number) => {
        const chartRef = chartsRef.current[index];

        if (chartRef) {
          chartRef.exportData();
        }
      },
    }),
    [],
  );

  const {
    annotations,
    fetchAnnotations,
    isAnnotationsLoading,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useAnnotations();

  const { chartConfigs } = useChartState();

  if (doNotShowCharts) {
    return null;
  }

  return (
    <div>
      <div className="flex flex-col space-y-2">
        {chartConfigs.map((chartConfig, chartIndex) => (
          <Chart
            ref={(ref) => setChartRef(chartIndex, ref)}
            key={chartIndex}
            chartConfig={chartConfig}
            chartIndex={chartIndex}
          />
        ))}
      </div>
      <hr className="border border-gray mt-2 mb-0" />
      <AddChartButton />
      <AnnotationsFlyout
        annotations={annotations}
        fetchAnnotations={fetchAnnotations}
        isAnnotationsLoading={isAnnotationsLoading}
        createAnnotation={createAnnotation}
        updateAnnotation={updateAnnotation}
        deleteAnnotation={deleteAnnotation}
      />
    </div>
  );
});
